import { safeRelativePath } from '@clockwise/shared';
import { inferAdditionalFields } from 'better-auth/client/plugins';
import { createAuthClient } from 'better-auth/react';
import { getOptionalEnv } from './env';

const API_URL = getOptionalEnv('VITE_API_URL') ?? 'http://localhost:4801';

export const authClient = createAuthClient({
  baseURL: API_URL,
  fetchOptions: { credentials: 'include' },
  plugins: [
    inferAdditionalFields({
      user: {
        tenantId: { type: 'string', required: false },
      },
    }),
  ],
});

export const { signOut, useSession, getSession } = authClient;

/**
 * Start Google OAuth. On success, return to `next` (validated as same-origin
 * relative). On error, return to `/login?error=...` so the login page can
 * surface the message.
 */
export async function signInWithGoogle(next?: string | null): Promise<void> {
  const webOrigin = window.location.origin;
  const target = safeRelativePath(next, webOrigin);
  await authClient.signIn.social({
    provider: 'google',
    callbackURL: `${webOrigin}${target}`,
    errorCallbackURL: `${webOrigin}/login`,
  });
}

/**
 * Sign in with email + password. Only works where the API runs with
 * `PASSWORD_AUTH_ENABLED=true` — a non-production affordance so QA automation
 * doesn't have to drive Google's consent screen. See
 * apps/api/src/auth/password-auth.ts.
 *
 * Unlike `signInWithGoogle`, this does not navigate: BetterAuth answers with
 * the session cookie in the same response, so the caller routes on success.
 */
export async function signInWithPassword(email: string, password: string): Promise<void> {
  const { error } = await authClient.signIn.email({ email, password });
  if (error) {
    // BetterAuth answers a bad email and a bad password identically, on
    // purpose — don't dress the message up into something that distinguishes
    // them.
    throw new Error(error.message ?? 'Invalid email or password');
  }
}

export interface AuthMethods {
  google: boolean;
  password: boolean;
}

/**
 * Ask the API which sign-in methods this environment offers.
 *
 * Deliberately a request rather than a build-time flag: the web image is built
 * once and promoted from QA to production, so anything baked into the bundle
 * would claim password auth in both. Falls back to Google-only, which is the
 * safe answer and what production always returns anyway.
 */
export async function fetchAuthMethods(): Promise<AuthMethods> {
  try {
    const res = await fetch(`${API_URL}/api/auth-methods`, { credentials: 'include' });
    if (!res.ok) return { google: true, password: false };
    const body = (await res.json()) as { data?: Partial<AuthMethods> };
    return { google: true, password: body.data?.password === true };
  } catch {
    return { google: true, password: false };
  }
}
